import { Typography } from "@mui/material"

/**
 * Circular progress indicator component.
 *
 * @component
 * @param {Object} props - The props for Progress.
 * @param {Number} [props.size=150] - The diameter of the progress circle in pixels.
 * @param {Number} [props.progress=0] - The progress value (0-100).
 * @param {Number} [props.trackWidth=10] - The width of the background track.
 * @param {String} [props.secondaryColor="#ddd"] - The color of the background track.
 * @param {Number} [props.indicatorWidth=10] - The width of the progress indicator.
 * @param {String} [props.color="#273679"] - The color of the progress indicator.
 * @param {String} [props.indicatorCap="round"] - The stroke linecap for the indicator (e.g., "round", "butt").
 * @param {String} [props.label="Progress"] - The label to display inside the circle.
 * @param {String} [props.labelColor="#333"] - The color of the label text.
 * @param {Boolean} [props.spinnerMode=false] - If true, shows a spinning animation instead of progress.
 * @param {Number} [props.spinnerSpeed=1] - The speed of the spinner animation (seconds per rotation).
 * @param {Boolean} [props.showLabel=false] - Whether to show the label text.
 * @param {Boolean} [props.showProgress=false] - Whether to show the progress text.
 * @param {React.CSSProperties} [props.labelStyles={}] - Label text styles.
 * @param {React.CSSProperties} [props.progressTextStyles={}] - Progress text styles.
 * @param {React.ReactNode} [props.icon] - Optional icon to display in the center instead of the label.
 * @returns {JSX.Element} The rendered Progress component.
 */
const Progress = ( {
    size = 150,
    progress = 0,
    trackWidth = 10,
    secondaryColor = `#ddd`,
    indicatorWidth = 10,
    color = `#273679`,
    indicatorCap = `round`,
    label = `Progress`,
    labelColor = `#333`,
    spinnerMode = false,
    spinnerSpeed = 1,
    showLabel = false,
    showProgress = false,
    icon = undefined,
    labelStyles = {},
    progressTextStyles = {}
} ) => {

    const strokeMax = Math.max( trackWidth, indicatorWidth ),
        center = size / 2,
        radius = Math.max( center - strokeMax / 2, 0 ),
        dashArray = 2 * Math.PI * radius,
        clamped = Math.min( Math.max( progress, 0 ), 100 ),
        dashOffset = dashArray * ( ( 100 - clamped ) / 100 )

    const showText = !spinnerMode

    return (
        <div
            className="svg-pi-wrapper"
            style={{ width: size, height: size }}
        >
            <svg className="svg-pi" width={size} height={size}>
                <circle
                    className="svg-pi-track"
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="transparent"
                    stroke={secondaryColor}
                    strokeWidth={trackWidth}
                />
                <circle
                    className={`svg-pi-indicator ${spinnerMode ? "svg-pi-indicator--spinner" : ""}`}
                    style={{ animationDuration: `${spinnerSpeed}s` }}
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="transparent"
                    stroke={color}
                    strokeWidth={indicatorWidth}
                    strokeDasharray={dashArray}
                    strokeDashoffset={dashOffset}
                    strokeLinecap={indicatorCap}
                />
            </svg>

            <div className="svg-pi-label" style={{ color: labelColor }}>
                {icon ? (
                    <Typography component="span" variant="body2" className="svg-pi-label__icon">
                        {icon}
                    </Typography>
                ) : showText && (
                    <>
                        {showLabel && !!label?.length && (
                            <Typography component="span" sx={labelStyles} variant="body2" className="svg-pi-label__loading">
                                {label}
                            </Typography>
                        )}

                        {showProgress && (
                            <Typography component="span" sx={progressTextStyles} variant="body2" className="svg-pi-label__progress">
                                {`${clamped}%`}
                            </Typography>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default Progress